import React from 'react';
import { useIntl } from 'react-intl';
import { FormDefinition } from 'store/booking-add-ons/types';
import { VendorFormRenderer } from 'components/vendor-form-renderer/vendor-form-renderer';
import { FormRendererValidationState } from 'components/vendor-form-renderer/types';
import { initialsOf } from 'utils/initials';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { AddOnPartyIcon } from 'components/add-on-party-icon';

import { PartyInitials, PartyIntro, QuestionsHeader } from './styles';
import { Title } from '../styles';

interface AddOnQuestionsProps {
  definition: FormDefinition;
  values: Record<string, unknown>;
  /* The provider whose questions these are. Absent in production when a company name does not resolve. */
  partyName?: string;
  /* VIS-2193. The provider's logo from helix (helix#11201), when it has one. */
  partyLogoUrl?: string;
  onChange: (values: Record<string, unknown>) => void;
  onValidate: (state: FormRendererValidationState) => void;
  /* A line in the selection carries no price, so these answers go out as a quote request. */
  hasUnpriceable?: boolean;
}

/*
 * RES-1357. A quote selection is the same form asked for a different reason, so only the sentence
 * moves. Exported and pure so the dialog reaches the same answer without a second copy of the rule,
 * as `ctaMessage` and `summaryAmount` already are.
 */
/*
 * Spelled out rather than composed from a suffix: the gate and the locale expert both find these by
 * grepping the literal, and a built string is invisible to them.
 */
const INTRO_IDS = {
  quote: { named: 'add_on_quote_intro', generic: 'add_on_quote_intro_generic' },
  order: { named: 'add_on_questions_intro', generic: 'add_on_questions_intro_generic' },
} as const;

export const questionsIntroId = (hasUnpriceable: boolean, hasPartyName: boolean): string =>
  INTRO_IDS[hasUnpriceable ? 'quote' : 'order'][hasPartyName ? 'named' : 'generic'];

/*
 * RES-1057 — the vendor's questions, as their own screen.
 *
 * ⛔ A SCREEN, NOT A ROUTE. The design asks for a separate screen and this renders one — title, back
 * chevron, its own CTA — but the step switches to it with local state rather than a sibling route,
 * because `pathname` is load-bearing on this surface in two ways a new segment would silently break:
 * `onSkip` and the eligibility bounce both rewrite the add-on segment by SUBSTRING to reach the
 * receipt, and `payment-content` decides whether to hide the native mobile header by matching path
 * substrings. A sibling segment satisfies neither, and both failures are invisible until a device.
 * Nothing is lost: a deep link here is something this surface explicitly does not want, since every
 * gate on the step is written to fail closed.
 *
 * ⚠️ ONE party block, never several. The mock shows two because the Service Request entry point can
 * span vendors; a booking carries exactly one party, enforced both client- and server-side, so there
 * is exactly one form and the copy is singular.
 */
export const AddOnQuestions = ({
  definition,
  values,
  partyName,
  partyLogoUrl,
  onChange,
  onValidate,
  hasUnpriceable = false,
}: AddOnQuestionsProps): JSX.Element => {
  const intl = useIntl();
  /* VIS-2193. The shared vendor mark (logo, or initials) behind `show-product-images`, as on the picker. */
  const { showProductImages } = useFlags();

  return (
    <div data-testid="booking-add-on-questions">
      {/*
        RES-1336 — the back control was HERE, and is now the wrapper's: this screen declares its back
        target through the step header and the modal header draws the one arrow. It used to be drawn
        locally because the wrapper's only back prop meant something else on the other eight routes —
        still true, which is why the header is scoped to this route rather than reusing that prop.

        The step's own name went with it. This read "Add-on questions" while the header above it said
        "Checkout" — two names for one screen, both on screen at once. The header keeps the step name
        and the provider below is the heading, which is what the design puts here and the thing the
        tenant actually needs: whose questions these are.

        ⛔ A HEADING EITHER WAY. RCL renders a modal's `title` as a styled div, so the header's title
        is not one; the fallback below carries it when the company name does not resolve.
      */}
      {/*
        ⛔ WITHOUT THIS THE SCREEN CAN CARRY NO PROVIDER IDENTITY AT ALL. The name used to appear only
        inside the intro string's `{vendor}` placeholder — so in the fallback case, which happens in
        production when a company name does not resolve, a tenant filled in a form with nothing on
        screen saying whose questions they were. The initials bubble is dropped in that case: one
        built from an absent name is an empty circle, which reads as broken rather than absent.
      */}
      {partyName ? (
        <QuestionsHeader data-testid="booking-add-on-questions-party">
          {showProductImages ? (
            <AddOnPartyIcon name={partyName} logoUrl={partyLogoUrl} size="md" />
          ) : (
            <PartyInitials aria-hidden="true">{initialsOf(partyName)}</PartyInitials>
          )}
          <Title as="h1">{partyName}</Title>
        </QuestionsHeader>
      ) : (
        <QuestionsHeader>
          <Title as="h1">{intl.formatMessage({ id: 'add_on_questions_title' })}</Title>
        </QuestionsHeader>
      )}
      {/*
        Why they are being asked at all. Falls back to a party-less string when the company name does
        not resolve — which happens in production — rather than rendering an empty name or, worse,
        disappearing and leaving a form with no explanation attached to it.
      */}
      <PartyIntro data-testid="booking-add-on-questions-intro">
        {intl.formatMessage({ id: questionsIntroId(hasUnpriceable, Boolean(partyName)) }, { vendor: partyName })}
      </PartyIntro>
      <VendorFormRenderer definition={definition} values={values} onChange={onChange} onValidate={onValidate} />
    </div>
  );
};
